import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Download, Lock, Unlock, Loader2, FileText, AlertCircle } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

export default function PdfToolsPanel() {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [stripXfa, setStripXfa] = useState(true); // Default to true
  const [unlocking, setUnlocking] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);

  const handleUnlock = async (withPassword = false) => {
    if (!file) {
      toast.error('Please select a PDF file');
      return;
    }

    setUnlocking(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('strip_xfa', stripXfa.toString());
    
    if (withPassword && password) {
      formData.append('password', password);
    }

    try {
      const endpoint = withPassword ? '/pdf-tools/unlock-with-password' : '/pdf-tools/unlock';
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        
        if (response.status === 400 && error.detail.includes('password')) {
          // PDF requires password
          setNeedsPassword(true);
          toast.error('This PDF requires a password');
          return;
        } else if (response.status === 401) {
          toast.error('Incorrect password');
          return;
        }
        
        throw new Error(error.detail || 'Failed to unlock PDF');
      }

      // Download the unlocked PDF
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_unlocked.pdf');
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF unlocked successfully!');
      
      // Reset form
      setFile(null);
      setPassword('');
      setNeedsPassword(false);
      
    } catch (error) {
      toast.error(error.message || 'Failed to unlock PDF');
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">PDF Tools</h2>
        <p className="text-sm text-slate-500">Utility tools for PDF processing</p>
      </div>

      {/* PDF Unlock Tool */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Unlock className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Unlock PDF</h3>
            <p className="text-sm text-slate-500">Remove encryption from password-protected PDFs</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* File Upload */}
          <div>
            <Label className="text-sm text-slate-600">Select PDF File</Label>
            <div className="mt-2 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => {
                  setFile(e.target.files[0]);
                  setNeedsPassword(false);
                  setPassword('');
                }}
                className="hidden"
                id="pdf-upload"
              />
              <label htmlFor="pdf-upload" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600 font-medium">Click to upload PDF</p>
                    <p className="text-xs text-slate-400 mt-1">Select an encrypted PDF file</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Password Input (conditional) */}
          {needsPassword && (
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="flex items-start gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Password Required</p>
                  <p className="text-xs text-amber-700">This PDF is password-protected</p>
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-600">PDF Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter PDF password"
                  className="mt-1"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && password) {
                      handleUnlock(true);
                    }
                  }}
                />
              </div>
            </div>
          )}

          {/* Strip XFA Option */}
          <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200">
            <input
              type="checkbox"
              id="strip-xfa"
              checked={stripXfa}
              onChange={(e) => setStripXfa(e.target.checked)}
              className="mt-1 w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="strip-xfa" className="flex-1 cursor-pointer">
              <p className="text-sm font-medium text-blue-900">Strip XFA Data</p>
              <p className="text-xs text-blue-700 mt-1">
                Removes XFA layer and forces all PDF viewers to use AcroForm fields. 
                Recommended for California law forms to ensure Adobe Acrobat compatibility.
              </p>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => handleUnlock(needsPassword && password ? true : false)}
              disabled={!file || unlocking || (needsPassword && !password)}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {unlocking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Unlocking...</>
              ) : (
                <><Unlock className="w-4 h-4 mr-2" /> Unlock PDF</>
              )}
            </Button>
            {file && (
              <Button
                variant="outline"
                onClick={() => {
                  setFile(null);
                  setPassword('');
                  setNeedsPassword(false);
                }}
              >
                Clear
              </Button>
            )}
          </div>

          {/* Info */}
          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
            <p className="text-xs text-slate-600">
              <strong>How it works:</strong> Upload an encrypted PDF. If it requires a password, you'll be prompted to enter it. 
              The tool will remove encryption and download the file with '_unlocked' suffix.
            </p>
            <p className="text-xs text-slate-500 mt-2">
              <Lock className="w-3 h-3 inline mr-1" />
              Files are processed in-memory and not stored on the server.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
